/**
 * Timezone utilities for appointment handling
 */

/**
 * Create a timezone-aware date from date and time strings
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {string} timeStr - Time string in HH:MM format
 * @returns {Date} Local timezone date object
 */
export function createLocalDateTime(dateStr, timeStr) {
  // Validate inputs
  if (!dateStr || !timeStr) {
    console.warn('createLocalDateTime: Missing dateStr or timeStr', { dateStr, timeStr });
    return new Date(); // Return current date as fallback
  }
  
  // Parse the date and time components
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hours, minutes] = timeStr.split(':').map(Number);
  
  // Validate parsed values
  if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hours) || isNaN(minutes)) {
    console.warn('createLocalDateTime: Invalid date/time format', { dateStr, timeStr });
    return new Date(); // Return current date as fallback
  }
  
  // Create date in local timezone (not UTC)
  return new Date(year, month - 1, day, hours, minutes, 0, 0);
}

/**
 * Format a date object to local date string (YYYY-MM-DD)
 * @param {Date} date - Date object
 * @returns {string} Date string in YYYY-MM-DD format
 */
export function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format a date object to local time string (HH:MM)
 * @param {Date} date - Date object
 * @returns {string} Time string in HH:MM format
 */
export function formatLocalTime(date) {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Check if two time periods overlap
 * @param {Date} start1 - Start of first period
 * @param {Date} end1 - End of first period
 * @param {Date} start2 - Start of second period
 * @param {Date} end2 - End of second period
 * @returns {boolean} True if periods overlap
 */
export function timePeriodsOverlap(start1, end1, start2, end2) {
  return start1 < end2 && end1 > start2;
}

/**
 * Add minutes to a date
 * @param {Date} date - Base date
 * @param {number} minutes - Minutes to add
 * @returns {Date} New date with minutes added
 */
export function addMinutes(date, minutes) {
  return new Date(date.getTime() + (minutes * 60000));
}

/**
 * Common timezone options for dropdown selection
 */
export const TIMEZONE_OPTIONS = [
  // Major business timezones
  { value: 'America/New_York', label: 'Eastern Time (New York)', region: 'Americas' },
  { value: 'America/Chicago', label: 'Central Time (Chicago)', region: 'Americas' },
  { value: 'America/Denver', label: 'Mountain Time (Denver)', region: 'Americas' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)', region: 'Americas' },
  { value: 'America/Toronto', label: 'Eastern Time (Toronto)', region: 'Americas' },
  { value: 'America/Vancouver', label: 'Pacific Time (Vancouver)', region: 'Americas' },
  
  // Europe
  { value: 'Europe/London', label: 'Greenwich Mean Time (London)', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Central European Time (Paris)', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Central European Time (Berlin)', region: 'Europe' },
  { value: 'Europe/Rome', label: 'Central European Time (Rome)', region: 'Europe' },
  { value: 'Europe/Madrid', label: 'Central European Time (Madrid)', region: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'Central European Time (Amsterdam)', region: 'Europe' },
  { value: 'Europe/Zurich', label: 'Central European Time (Zurich)', region: 'Europe' },
  { value: 'Europe/Stockholm', label: 'Central European Time (Stockholm)', region: 'Europe' },
  { value: 'Europe/Oslo', label: 'Central European Time (Oslo)', region: 'Europe' },
  { value: 'Europe/Helsinki', label: 'Eastern European Time (Helsinki)', region: 'Europe' },
  { value: 'Europe/Warsaw', label: 'Central European Time (Warsaw)', region: 'Europe' },
  { value: 'Europe/Prague', label: 'Central European Time (Prague)', region: 'Europe' },
  { value: 'Europe/Vienna', label: 'Central European Time (Vienna)', region: 'Europe' },
  { value: 'Europe/Budapest', label: 'Central European Time (Budapest)', region: 'Europe' },
  { value: 'Europe/Athens', label: 'Eastern European Time (Athens)', region: 'Europe' },
  { value: 'Europe/Istanbul', label: 'Turkey Time (Istanbul)', region: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow Standard Time (Moscow)', region: 'Europe' },
  
  // Asia-Pacific
  { value: 'Asia/Singapore', label: 'Singapore Standard Time (Singapore)', region: 'Asia-Pacific' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong Standard Time (Hong Kong)', region: 'Asia-Pacific' },
  { value: 'Asia/Shanghai', label: 'China Standard Time (Shanghai)', region: 'Asia-Pacific' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (Tokyo)', region: 'Asia-Pacific' },
  { value: 'Asia/Seoul', label: 'Korea Standard Time (Seoul)', region: 'Asia-Pacific' },
  { value: 'Asia/Taipei', label: 'Taipei Standard Time (Taipei)', region: 'Asia-Pacific' },
  { value: 'Asia/Bangkok', label: 'Indochina Time (Bangkok)', region: 'Asia-Pacific' },
  { value: 'Asia/Jakarta', label: 'Western Indonesia Time (Jakarta)', region: 'Asia-Pacific' },
  { value: 'Asia/Kuala_Lumpur', label: 'Malaysia Time (Kuala Lumpur)', region: 'Asia-Pacific' },
  { value: 'Asia/Manila', label: 'Philippine Standard Time (Manila)', region: 'Asia-Pacific' },
  { value: 'Asia/Kolkata', label: 'India Standard Time (Mumbai)', region: 'Asia-Pacific' },
  { value: 'Asia/Dubai', label: 'Gulf Standard Time (Dubai)', region: 'Asia-Pacific' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (Sydney)', region: 'Asia-Pacific' },
  { value: 'Australia/Melbourne', label: 'Australian Eastern Time (Melbourne)', region: 'Asia-Pacific' },
  { value: 'Australia/Perth', label: 'Australian Western Time (Perth)', region: 'Asia-Pacific' },
  { value: 'Pacific/Auckland', label: 'New Zealand Standard Time (Auckland)', region: 'Asia-Pacific' },
  
  // Africa & Middle East
  { value: 'Africa/Cairo', label: 'Eastern European Time (Cairo)', region: 'Africa' },
  { value: 'Africa/Johannesburg', label: 'South Africa Standard Time (Johannesburg)', region: 'Africa' },
  { value: 'Africa/Lagos', label: 'West Africa Time (Lagos)', region: 'Africa' },
  { value: 'Africa/Nairobi', label: 'East Africa Time (Nairobi)', region: 'Africa' },
];

/**
 * Get timezone options grouped by region
 */
export function getTimezoneOptionsByRegion() {
  const grouped = {};
  TIMEZONE_OPTIONS.forEach(tz => {
    if (!grouped[tz.region]) {
      grouped[tz.region] = [];
    }
    grouped[tz.region].push(tz);
  });
  return grouped;
}

/**
 * Convert a date/time from one timezone to another
 * @param {Date} date - Source date
 * @param {string} fromTimezone - Source timezone (IANA identifier)
 * @param {string} toTimezone - Target timezone (IANA identifier)
 * @returns {Date} Converted date
 */
export function convertTimezone(date, fromTimezone, toTimezone) {
  try {
    // Create a date string in the source timezone
    const sourceTime = new Intl.DateTimeFormat('en-CA', {
      timeZone: fromTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).format(date);
    
    // Parse the formatted string and create new date in target timezone
    const [datePart, timePart] = sourceTime.split(', ');
    const [year, month, day] = datePart.split('-');
    const [hour, minute, second] = timePart.split(':');
    
    // Create date assuming it's in the target timezone
    const targetDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
    
    return targetDate;
  } catch (error) {
    console.error('Timezone conversion error:', error);
    return date; // Return original date as fallback
  }
}

/**
 * Get the current user's timezone
 */
export function getUserTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Format time with timezone indicator
 * @param {Date} date - Date to format
 * @param {string} timezone - Timezone to display
 * @param {boolean} showTimezone - Whether to show timezone abbreviation
 */
export function formatTimeWithTimezone(date, timezone, showTimezone = true) {
  const timeStr = formatLocalTime(date);
  
  if (!showTimezone) return timeStr;
  
  try {
    const tzAbbr = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'short'
    }).formatToParts(date).find(part => part.type === 'timeZoneName')?.value || timezone;
    
    return `${timeStr} ${tzAbbr}`;
  } catch (error) {
    return `${timeStr} (${timezone})`;
  }
}

/**
 * Check if two timezones are different
 */
export function timezonesAreDifferent(tz1, tz2) {
  if (!tz1 || !tz2) return false;
  return tz1 !== tz2;
}
